// ReconnectRecoveryProvider — при offline→online и при возврате приложения
// из фона рефетчит stalled queries и поднимает PowerSync.
// STUDYHUB_ONLINE_EVENT эмитит только network-status (единый источник).
// Offline-first UX plan фаза 0 + фикс «скелетон после reopen».

import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef } from "react";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { isAppVisible, onAppVisibilityChange } from "@/lib/app-visible";
import { connectPowerSync, peekPowerSync } from "@/lib/powersync/client";
import { isPowerSyncAuthSuspended } from "@/lib/powersync/jwt-provider";

/** Активные запросы, которые стоит дёрнуть после реконнекта/resume. */
function isStalledActiveQuery(q: {
	state: { status: string; data: unknown; fetchStatus: string };
}): boolean {
	const { status, data, fetchStatus } = q.state;
	if (status === "error") return true;
	if (data === undefined) return true;
	// Залипший fetch после фона (сокет/радио уснули) — отменяем и перезапускаем.
	if (fetchStatus === "fetching") return true;
	if (fetchStatus === "paused") return true;
	return false;
}

/**
 * Поднять синк после паузы — но только если он вообще должен работать.
 *
 * Провайдер живёт ВЫШЕ `AuthProvider` (см. `app-providers.tsx`), поэтому о
 * сессии он спрашивает не `useAuth()`, а сам синк: пауза аутентификации и есть
 * состояние «синка быть не должно» (гость, выход, отказ 401). Без этой проверки
 * возврат вкладки из фона воскрешал синк у гостя на публичной странице, и тот
 * уходил в вечный цикл 401 к `/api/sync/powersync/auth`.
 */
function reconnectSyncIfAllowed(): void {
	if (isPowerSyncAuthSuspended()) return;
	// Базы на вкладке нет — значит, и синка не было: поднимать нечего. Без
	// проверки `connectPowerSync()` открыл бы базу гостю в обход
	// `local-db-gate.ts`, например на возврате вкладки до ответа о сессии.
	if (!peekPowerSync()) return;
	void connectPowerSync().catch(() => undefined);
}

function recoverAfterResume(qc: ReturnType<typeof useQueryClient>): void {
	void qc.refetchQueries({
		type: "active",
		predicate: isStalledActiveQuery,
	});
	// Синк мог остаться отключённым (истёк токен, спящий сокет после фона).
	reconnectSyncIfAllowed();
}

export function ReconnectRecoveryProvider({ children }: { children: ReactNode }): ReactNode {
	const online = useOnlineStatus();
	const qc = useQueryClient();
	const wasOfflineRef = useRef(!online);
	const wasHiddenRef = useRef(!isAppVisible());
	const onlineRef = useRef(online);
	onlineRef.current = online;

	useEffect(() => {
		if (!online) {
			wasOfflineRef.current = true;
			return;
		}
		if (!wasOfflineRef.current) return;
		wasOfflineRef.current = false;
		recoverAfterResume(qc);
	}, [online, qc]);

	// Soft resume (Capacitor foreground / visibilitychange): сеть могла
	// остаться «online», но сокеты и залипшие fetch не оживают сами.
	useEffect(() => {
		return onAppVisibilityChange(() => {
			const visible = isAppVisible();
			if (!visible) {
				wasHiddenRef.current = true;
				return;
			}
			if (!wasHiddenRef.current) return;
			wasHiddenRef.current = false;
			if (!onlineRef.current) {
				// Точная проба ещё offline — PS всё равно пробуем; stalled
				// queries подхватит offline→online эффект выше.
				reconnectSyncIfAllowed();
				return;
			}
			recoverAfterResume(qc);
		});
	}, [qc]);

	return children;
}
